import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Menu, X } from "lucide-react";
import { ThemeToggle } from "./ThemeToggle";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

// Nav items shared between the desktop row and the mobile drawer. Home is first so the
// desktop nav has an explicit way back to `/app` beyond the logo click.
const NAV = [
  { to: "/app", label: "Home", testId: "nav-home" },
  { to: "/app/net-worth", label: "Net Worth", testId: "nav-net-worth" },
  { to: "/app/budget", label: "Budget", testId: "nav-budget" },
  { to: "/app/policies", label: "Policies", testId: "nav-policies" },
  { to: "/app/calculators", label: "Calculators", testId: "nav-calculators" },
  { to: "/app/chat", label: "Chat", testId: "nav-chat" },
  { to: "/profile", label: "Profile", testId: "nav-profile" },
];

// Show a back button on every authenticated screen — including the dashboard root — so
// users always have a clear way to step back up the stack.
function shouldShowBackButton() {
  return true;
}

export function Header() {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close the drawer whenever the route changes — otherwise navigating from inside
  // the drawer leaves it hanging open on the new page. `location.key` changes on every
  // navigation (even same-path pushes), so this covers repeat-taps on the current route.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname, location.key]);

  // Escape key closes the drawer — matches native `<dialog>` behaviour.
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  // Lock body scroll while the drawer is open on mobile so the underlying page doesn't
  // scroll behind it.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  async function handleLogout() {
    setDrawerOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  const showBack = isAuthenticated && shouldShowBackButton();

  return (
    <>
    <header className="sticky top-0 z-30 border-b border-border bg-bg/95 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-3 py-3 sm:px-4">
        <div className="flex min-w-0 items-center gap-2">
          {/* Back button — shown on every authenticated screen except the dashboard root.
             Uses browser history so it always feels natural. Visible on both mobile and desktop. */}
          {showBack && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="rounded-md p-1.5 text-muted hover:bg-elevated hover:text-text"
              data-testid="mobile-back-button"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}
          <Link to="/app" className="flex min-w-0 items-center gap-2">
            <img src="/logo-mark.png" alt="" className="h-6 w-6 object-contain sm:h-7 sm:w-7" />
            <span className="truncate font-serif text-base tracking-tight text-text sm:text-lg">
              The <span className="text-gold">Money</span> Gurukul
            </span>
          </Link>
        </div>

        {/* Desktop nav */}
        <div className="hidden items-center gap-3 sm:flex">
          {isAuthenticated && (
            <>
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="text-sm text-muted hover:text-text"
                  data-testid={item.testId}
                >
                  {item.label}
                </Link>
              ))}
              <ThemeToggle />
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout">
                Log out
              </Button>
            </>
          )}
          {!isAuthenticated && <ThemeToggle />}
        </div>

        {/* Mobile hamburger — only when authed. ThemeToggle sits beside it for quick access. */}
        {isAuthenticated && (
          <div className="flex items-center gap-1.5 sm:hidden">
            <ThemeToggle compact />
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              className="rounded-md p-2 text-muted hover:bg-elevated hover:text-text"
              data-testid="mobile-menu-button"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        )}
        {!isAuthenticated && <div className="sm:hidden"><ThemeToggle compact /></div>}
      </div>
    </header>

    {/* Mobile drawer — rendered OUTSIDE the <header> because the header's `backdrop-blur`
       creates a new containing block that would clip a fixed-positioned aside to the
       header's own height (~60px), hiding most of the nav on mobile.
       Z-index: backdrop z-50 sits above the bottom tab bar (also z-40) so the tab bar
       is dimmed and non-interactive while the drawer is open; aside is z-[60]. */}
    {drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm sm:hidden"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="fixed inset-y-0 right-0 z-[60] flex w-72 max-w-[85vw] flex-col border-l border-border bg-bg shadow-2xl sm:hidden"
            data-testid="mobile-drawer"
            role="dialog"
            aria-label="Navigation menu"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="font-serif text-sm text-muted">Menu</span>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="rounded-md p-1.5 text-muted hover:bg-elevated hover:text-text"
                data-testid="mobile-drawer-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setDrawerOpen(false)}
                  className="rounded-md px-3 py-2.5 text-sm text-text hover:bg-elevated"
                  data-testid={`drawer-${item.testId}`}
                >
                  {item.label}
                </Link>
              ))}
              <Link
                to="/my-scenarios"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md px-3 py-2.5 text-sm text-text hover:bg-elevated"
                data-testid="drawer-nav-scenarios"
              >
                My Scenarios
              </Link>
            </nav>
            <div className="flex items-center justify-between gap-2 border-t border-border p-3">
              <ThemeToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="drawer-nav-logout"
                className="flex-1"
              >
                Log out
              </Button>
            </div>
          </aside>
        </>
      )}
    </>
  );
}
